'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');
const libTaskCatalog = require('../access/Task-Catalog.js');
const libMasterListCatalog = require('../master-lists/Headlight-MasterLists.js');
const libTabBar = require('../views/TabBar.js');

const RAIL_MOUNT = '#Ent-Rail';
const RAIL_LIST_MOUNT = '#Ent-Rail-List';
const DETAIL_MOUNT = '#Ent-Detail';
const FORMS_LIST_MOUNT = '#Ent-Forms-List';
const MLIST_LIST_MOUNT = '#Ent-MList-List';
const TASKS_LIST_MOUNT = '#Ent-Tasks-List';   // the Features + Contract Administration tabs' scrollable task list
const STATUS_MOUNT = '#Ent-Status';           // the saving / error line (repainted in place so a toggle never rebuilds the detail)

// Managed permission-group name prefixes. Groups named with either prefix are lazily created + owned by this
// screen; they're hidden from the subject rail and indexed for state.
const TASK_PREFIX = 'Task-';         // Task-<Key>          — a feature task's capability bundle
const FORM_PREFIX = 'Form-';         // Form-<RNI.Hash>     — full access to one report/form (document type)
const MLIST_PREFIX = 'MList-';       // MList-<Entity>-<Action> — ONE capability (<Entity>-<Action>) on a master list

// Master-list access is granted per Meadow action. Read is the primary toggle (it's what un-hides the list in the
// navigation — the nav gate derives <Entity>-Read from the #/PSRS/<Entity>/List route); Create/Update/Delete sit
// behind an "Advanced" disclosure. One managed group per (entity, action), mirroring the Form-group model.
const MLIST_ACTIONS = [ 'Read', 'Create', 'Update', 'Delete' ];
const MLIST_ADV_ACTIONS = [ 'Create', 'Update', 'Delete' ];

// The dedicated Contract Administration tab renders exactly the tasks in this Area (the re-homed Pay & Funding
// list-management tasks + the funding association / module tasks); the Features tab excludes it.
const CONTRACT_ADMIN_AREA = 'Contract Administration';

// A report/form (ReportNamedInstance) capability hash is `DocumentType-<RNI.Hash>-<Action>` — mirrors the
// Access grid's reports/forms axis (Access-Matrix-Builder.reportCatalogSource). "Full access" is the four base
// report actions; finer control (individual actions, per-project scope, approval statuses) is the grid's job.
const REPORT_ENTITY_PREFIX = 'DocumentType-';
const FORM_ACTIONS = [ 'Create', 'View', 'Update', 'Delete' ];

// Elevating a USER (a person, not a role) to the CustomerAdmin config-admin tier. A broad task normally sets a
// Role/Group subject's own PermissionLevel; a user has none of their own, so we assign the shared, per-customer
// 'Customer Config Admin' group (PermissionLevel CustomerAdmin) — the SAME group the Grant Access screen
// manages, so the two screens stay in sync. Other broad tiers stay role/group-only.
const CONFIG_ADMIN_LEVEL = 'CustomerAdmin';
const CONFIG_ADMIN_GROUP_NAME = 'Customer Config Admin';

/**
 * Entitlements — the #/Entitlements screen. A task-based front end over the RBAC system: pick a subject (Role,
 * Permission Group, or User) and toggle human entitlements on/off; each toggle writes through the SAME grant path
 * the #/Access workspace uses — no matrix, no context theory. Two tabs: **Features** (the Task-Catalog.js task
 * bundles) and **Reports & Forms** (per-document-type full-access grants).
 *
 * Model:
 *   - A **feature task** is a lazily-created Permission Group named `Task-<Key>` carrying the task's capability
 *     bundle; a **form** is a lazily-created Permission Group named `Form-<RNI.Hash>` carrying full CRUD on that
 *     document type. Both are one-per-customer, shared across subjects, created on first use, never prospectively.
 *   - Turning one on:  Role/Group subject → compose the group in via **RoleEdge**;  User subject → assign it via
 *     **UserRoleJoin**. Off → remove that edge / assignment (the group stays for reuse).
 *   - **Broad tasks** (RequiresPermissionLevel, e.g. CustomerAdmin) set the subject's PermissionLevel directly for
 *     a Role/Group subject; they're offered only for Role/Group subjects (a person gets a tier by holding such a role).
 *
 * SECURITY / VERIFY: this writes real grants. The endpoint shapes below mirror the #/Access providers
 * (PermissionMatrix / GroupComposition / RoleAssignment) per the security map, but the round-trips MUST be
 * verified on QA against #/Access before this is trusted — I could not run it. Writing is gated to Role Managers
 * client-side; the server is authoritative.
 */
class EntitlementsProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._searchTimer = null;
		this._searchToken = 0;
	}

	get app() { return this.pict.PictApplication; }
	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get state()
	{
		if (!this.pict.AppData.Entitlements)
		{
			this.pict.AppData.Entitlements =
			{
				Loaded: false, Loading: false, Roles: [], Groups: [], Users: [], TaskGroupsByKey: {}, FormGroupsByHash: {},
				SubjectKind: '', SubjectID: 0, SubjectLabel: '', SubjectRecord: null, SubjectPermissionLevel: '', HeldRoleIDs: {}, SubjectSearch: '', Searching: false, ConfigAdminGroup: null,
				ActiveTasks: {}, Busy: false, Error: '', ConfirmBroad: '',
				Tab: 'features', Forms: [], FormsLoaded: false, FormsLoading: false, ActiveForms: {}, FormSearch: '',
				MasterListGroupsByKey: {}, ActiveMasterLists: {}, MasterListSearch: '', MasterListExpanded: {},
			};
		}
		return this.pict.AppData.Entitlements;
	}

	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID) : 0; }

	/**
	 * Customer-1 / role-5 super-user — the server's EndpointBase._isSuperUser definition, and the ONLY subject that
	 * keeps the full cross-customer rail. Deliberately NARROWER than AccessWorkspace-Provider.isSuperUser, which also
	 * counts anyone holding a `SuperAdminAccessRole` role: customer admins (e.g. LADOTD) hold that role, so counting
	 * it here would hand them every customer's roles — the exact cross-customer leak this scoping fixes.
	 */
	isSuperUser()
	{
		const tmpUser = this.pict.AppData.CurrentUser;
		if (!tmpUser) { return false; }
		return (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1);
	}

	/** UI gate — only a Role Manager (or super-user) may edit entitlements. Server stays authoritative. */
	canEdit()
	{
		const tmpUser = this.pict.AppData.CurrentUser;
		if (!tmpUser) { return false; }
		if ((Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1)) { return true; }
		const tmpRoles = tmpUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && ((pRole.PermissionLevel === 'RoleManager') || (pRole.PermissionLevel === 'SuperAdminUser')));
	}

	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_putJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.putJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }

	// ── Load ───────────────────────────────────────────────────────────────────

	loadAsync()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return; }
		tmpState.Loading = true;
		this.render();
		// The subject rail is server-side search (see setSubjectSearch/_searchSubjects) — with real data a customer
		// has thousands of users and roles, so we must NOT pre-load them and filter client-side (that only ever saw
		// the first page). Up front we load ONLY the managed permission groups — the Task-<Key> feature bundles and
		// Form-<RNI.Hash> full-access groups this screen lazily creates. That set is small and bounded (one row per
		// task/form per customer), and we need its full index so toggle-state reads true and _ensureTaskGroup /
		// _ensureFormGroup reuse an existing group instead of creating a duplicate. Managed groups live in the
		// caller's own customer (that's where _ensureTaskGroup creates them), so scope the lookup there.
		const tmpCustomer = this.callerCustomer();
		const tmpTaskURL = `Roles/FilteredTo/FBV~Name~LK~${encodeURIComponent(TASK_PREFIX + '%')}~FBV~IDCustomer~EQ~${tmpCustomer}~FBV~Deleted~EQ~0/0/500`;
		const tmpFormURL = `Roles/FilteredTo/FBV~Name~LK~${encodeURIComponent(FORM_PREFIX + '%')}~FBV~IDCustomer~EQ~${tmpCustomer}~FBV~Deleted~EQ~0/0/1000`;
		const tmpMListURL = `Roles/FilteredTo/FBV~Name~LK~${encodeURIComponent(MLIST_PREFIX + '%')}~FBV~IDCustomer~EQ~${tmpCustomer}~FBV~Deleted~EQ~0/0/1000`;
		Promise.all([ this._getJSON(tmpTaskURL), this._getJSON(tmpFormURL), this._getJSON(tmpMListURL) ]).then((pResults) =>
		{
			const tmpManaged = (Array.isArray(pResults[0]) ? pResults[0] : [])
				.concat(Array.isArray(pResults[1]) ? pResults[1] : [])
				.concat(Array.isArray(pResults[2]) ? pResults[2] : []);
			tmpState.TaskGroupsByKey = {};
			tmpState.FormGroupsByHash = {};
			tmpState.MasterListGroupsByKey = {};
			tmpManaged.forEach((pRole) =>
			{
				const tmpName = String((pRole && pRole.Name) || '');
				if (tmpName.indexOf(TASK_PREFIX) === 0) { tmpState.TaskGroupsByKey[tmpName.slice(TASK_PREFIX.length)] = pRole; }
				else if (tmpName.indexOf(MLIST_PREFIX) === 0) { tmpState.MasterListGroupsByKey[tmpName.slice(MLIST_PREFIX.length)] = pRole; }
				else if (tmpName.indexOf(FORM_PREFIX) === 0) { tmpState.FormGroupsByHash[tmpName.slice(FORM_PREFIX.length)] = pRole; }
			});
			// Subjects (Roles/Groups/Users) start empty; they're populated per-search from the server.
			tmpState.Roles = [];
			tmpState.Groups = [];
			tmpState.Users = [];
			tmpState.Loaded = true;
			tmpState.Loading = false;
			this.render();
		});
	}

	/** Select a subject and load which tasks are currently active on it. */
	selectSubject(pKind, pID)
	{
		const tmpState = this.state;
		tmpState.SubjectKind = pKind;
		tmpState.SubjectID = Number(pID);
		tmpState.Error = '';
		tmpState.ConfirmBroad = '';
		tmpState.ActiveTasks = {};
		tmpState.ActiveForms = {};
		tmpState.HeldRoleIDs = {};
		tmpState.SubjectPermissionLevel = '';
		const tmpSubject = this._subject(pKind, Number(pID));
		// Cache the selected record: the rail is live search, so the results list it came from can be replaced by
		// the next keystroke — _subject() falls back to this so writes + reloads still resolve the subject.
		tmpState.SubjectRecord = tmpSubject;
		tmpState.SubjectLabel = tmpSubject ? (tmpSubject.Name || libUserSearch.userLabel(tmpSubject) || ('#' + pID)) : ('#' + pID);
		if (pKind !== 'User') { tmpState.SubjectPermissionLevel = (tmpSubject && tmpSubject.PermissionLevel) || ''; }
		this.render();
		this._loadSubjectTasks(pKind, Number(pID));
	}

	_subject(pKind, pID)
	{
		const tmpState = this.state;
		const tmpList = (pKind === 'User') ? tmpState.Users : (pKind === 'Group') ? tmpState.Groups : tmpState.Roles;
		const tmpKey = (pKind === 'User') ? 'IDUser' : 'IDRole';
		const tmpFound = (tmpList || []).find((pItem) => Number(pItem[tmpKey]) === Number(pID));
		if (tmpFound) { return tmpFound; }
		// The search results may have moved on since selection; fall back to the record captured at select time.
		if (tmpState.SubjectRecord && (tmpState.SubjectKind === pKind) && (Number(tmpState.SubjectID) === Number(pID))) { return tmpState.SubjectRecord; }
		return null;
	}

	/**
	 * Which task groups the subject already holds. Role/Group → its RoleEdge children; User → its assigned roles.
	 * A task is "active" when the subject holds its Task-<Key> group (or, for a broad task, when a Role/Group
	 * subject's own PermissionLevel matches).
	 */
	_loadSubjectTasks(pKind, pID, pInPlace)
	{
		const tmpState = this.state;
		const tmpURL = (pKind === 'User')
			? `UserRoleJoins/FilteredTo/FBV~IDUser~EQ~${pID}`
			: `RoleEdges/FilteredTo/FBV~ParentIDRole~EQ~${pID}`;
		// For a User subject, also resolve the shared 'Customer Config Admin' group in THAT user's own customer,
		// so the broad config-admin task can reflect (and toggle) whether the person holds it.
		const tmpSubject = this._subject(pKind, pID);
		const tmpSubjectCustomer = tmpSubject ? Number(tmpSubject.IDCustomer) : this.callerCustomer();
		const tmpConfigGroupP = (pKind === 'User')
			? this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${tmpSubjectCustomer}~FBV~Name~EQ~${encodeURIComponent(CONFIG_ADMIN_GROUP_NAME)}~FBV~Deleted~EQ~0/0/1`)
			: Promise.resolve(null);
		Promise.all([ this._getJSON(tmpURL), tmpConfigGroupP ]).then((pResults) =>
		{
			if ((tmpState.SubjectKind !== pKind) || (tmpState.SubjectID !== pID)) { return; }
			const pRows = pResults[0];
			tmpState.ConfigAdminGroup = (Array.isArray(pResults[1]) ? pResults[1] : []).find((pRole) => pRole && pRole.IDRole) || null;
			const tmpHeldRoleIDs = {};
			(Array.isArray(pRows) ? pRows : []).forEach((pRow) => { const tmpChild = (pKind === 'User') ? pRow.IDRole : pRow.ChildIDRole; if (tmpChild) { tmpHeldRoleIDs[Number(tmpChild)] = pRow; } });
			tmpState.HeldRoleIDs = tmpHeldRoleIDs;   // shared by the feature tasks + the reports/forms tab
			const tmpConfigLink = tmpState.ConfigAdminGroup ? tmpHeldRoleIDs[Number(tmpState.ConfigAdminGroup.IDRole)] : null;
			const tmpActive = {};
			libTaskCatalog.getTasks().forEach((pTask) =>
			{
				if (pTask.RequiresPermissionLevel && (pKind !== 'User'))
				{
					// Role/Group subject: the tier is the subject's own PermissionLevel.
					tmpActive[pTask.Key] = { On: (tmpState.SubjectPermissionLevel === pTask.RequiresPermissionLevel), Link: null };
					return;
				}
				if (pTask.RequiresPermissionLevel && (pKind === 'User'))
				{
					// User subject: only the CustomerAdmin config-admin tier is grantable to a person (the shared
					// group); other tiers stay role/group-only, so they read Off.
					const tmpIsConfig = (pTask.RequiresPermissionLevel === CONFIG_ADMIN_LEVEL);
					tmpActive[pTask.Key] = { On: (tmpIsConfig && !!tmpConfigLink), Link: (tmpIsConfig ? (tmpConfigLink || null) : null) };
					return;
				}
				const tmpGroup = tmpState.TaskGroupsByKey[pTask.Key];
				const tmpLink = tmpGroup ? tmpHeldRoleIDs[Number(tmpGroup.IDRole)] : null;
				tmpActive[pTask.Key] = { On: !!tmpLink, Link: tmpLink || null };
			});
			tmpState.ActiveTasks = tmpActive;
			this._computeActiveForms();
			this._computeActiveMasterLists();
			// After a toggle we repaint only the active list (keeps tab + scroll); on a fresh subject load, full render.
			if (pInPlace) { this._repaintActiveList(); } else { this.render(); }
		});
	}

	// ── Toggle a task ────────────────────────────────────────────────────────────

	toggleTask(pKey)
	{
		const tmpState = this.state;
		if (!this.canEdit() || !tmpState.SubjectID || tmpState.Busy) { return; }
		const tmpTask = libTaskCatalog.getTask(pKey);
		if (!tmpTask || tmpTask.Pending) { return; }
		const tmpEntry = tmpState.ActiveTasks[pKey] || { On: false };
		// A broad (PermissionLevel) task turning ON gets a confirm step; otherwise apply immediately.
		if (tmpTask.Broad && !tmpEntry.On && (tmpState.ConfirmBroad !== pKey)) { tmpState.ConfirmBroad = pKey; this._repaintActiveList(); return; }
		tmpState.ConfirmBroad = '';
		tmpState.Busy = true;
		tmpState.Error = '';
		this._repaintActiveList();
		const fThen = (pOk) =>
		{
			tmpState.Busy = false;
			if (!pOk) { tmpState.Error = 'The server rejected the change (you may lack Permission-Grant in this context). Nothing was changed.'; this._repaintActiveList(); return; }
			// Reload the subject's state from the server so the toggles reflect the truth.
			if (tmpState.SubjectKind !== 'User') { const tmpSubject = this._subject(tmpState.SubjectKind, tmpState.SubjectID); tmpState.SubjectPermissionLevel = (tmpSubject && tmpSubject.PermissionLevel) || tmpState.SubjectPermissionLevel; }
			this._loadSubjectTasks(tmpState.SubjectKind, tmpState.SubjectID, true);
		};
		if (tmpTask.RequiresPermissionLevel && (tmpState.SubjectKind !== 'User')) { this._applyBroad(tmpTask, !tmpEntry.On).then(fThen); }
		else if (tmpTask.RequiresPermissionLevel && (tmpTask.RequiresPermissionLevel === CONFIG_ADMIN_LEVEL) && (tmpState.SubjectKind === 'User')) { this._applyConfigAdminForUser(!tmpEntry.On).then(fThen); }
		else if (tmpTask.RequiresPermissionLevel && (tmpState.SubjectKind === 'User')) { tmpState.Busy = false; this._repaintActiveList(); }   // other tiers are role/group-only (toggle is disabled)
		else { this._applyCapabilityTask(tmpTask, tmpEntry, !tmpEntry.On).then(fThen); }
	}

	/**
	 * User subject: grant/remove the shared 'Customer Config Admin' group (PermissionLevel CustomerAdmin) in the
	 * user's own customer — the same group #/GrantAccess manages, so a person elevated on either screen reads the
	 * same on both. @param {boolean} pOn
	 */
	_applyConfigAdminForUser(pOn)
	{
		const tmpState = this.state;
		const tmpSubject = this._subject('User', tmpState.SubjectID);
		if (!tmpSubject) { return Promise.resolve(false); }
		const tmpIDUser = Number(tmpSubject.IDUser);
		const tmpIDCustomer = Number(tmpSubject.IDCustomer);
		if (pOn)
		{
			return this._ensureConfigAdminGroup(tmpIDCustomer).then((tmpGroup) =>
			{
				if (!tmpGroup || !tmpGroup.IDRole) { return false; }
				return this._putJSON('Permissions/Role/For/User', { IDRole: Number(tmpGroup.IDRole), IDUser: tmpIDUser, Context: 'Customer', IDContext: tmpIDCustomer }).then((pRes) => !pRes.error);
			});
		}
		// Off — remove the user's assignment to the group (the group itself is left for reuse).
		const tmpLink = (tmpState.ConfigAdminGroup && tmpState.HeldRoleIDs) ? tmpState.HeldRoleIDs[Number(tmpState.ConfigAdminGroup.IDRole)] : null;
		if (!tmpLink) { return Promise.resolve(true); }
		return this._unlinkSubjectFromGroup(tmpLink);
	}

	/** Find (or create) the per-customer 'Customer Config Admin' Permission Group (PermissionLevel CustomerAdmin). */
	_ensureConfigAdminGroup(pIDCustomer)
	{
		return this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${pIDCustomer}~FBV~Name~EQ~${encodeURIComponent(CONFIG_ADMIN_GROUP_NAME)}~FBV~Deleted~EQ~0/0/1`).then((pRoles) =>
		{
			const tmpExisting = (Array.isArray(pRoles) ? pRoles : []).find((pRole) => pRole && pRole.IDRole);
			if (tmpExisting) { return tmpExisting; }
			return this._postJSON('Role',
				{
					Name: CONFIG_ADMIN_GROUP_NAME, Type: 'PermissionGroup', PermissionLevel: CONFIG_ADMIN_LEVEL,
					Description: 'Grants configuration-admin rights (PermissionLevel CustomerAdmin) for this customer. Managed by Grant Access / Entitlements.',
					IDCustomer: pIDCustomer,
				}).then((pRes) => (pRes && !pRes.error && pRes.result) ? pRes.result : null);
		});
	}

	/** Broad task: set (or clear) the subject role/group's PermissionLevel. */
	_applyBroad(pTask, pOn)
	{
		const tmpState = this.state;
		const tmpSubject = this._subject(tmpState.SubjectKind, tmpState.SubjectID);
		if (!tmpSubject) { return Promise.resolve(false); }
		const tmpBody = Object.assign({}, tmpSubject, { PermissionLevel: pOn ? pTask.RequiresPermissionLevel : '' });
		return this._putJSON('Role', tmpBody).then((pRes) =>
		{
			if (pRes.error) { return false; }
			tmpSubject.PermissionLevel = tmpBody.PermissionLevel;   // keep the cached subject in step
			return true;
		});
	}

	/** Capability task: ensure the Task-<Key> group exists + granted, then add/remove the subject's link to it. */
	_applyCapabilityTask(pTask, pEntry, pOn)
	{
		const tmpState = this.state;
		if (pOn)
		{
			return this._ensureTaskGroup(pTask).then((pGroup) =>
			{
				if (!pGroup) { return false; }
				return this._linkSubjectToGroup(pGroup.IDRole).then((pOk) => pOk);
			});
		}
		// Off — remove the link (leave the shared group for reuse).
		return this._unlinkSubjectFromGroup(pEntry.Link);
	}

	/** Find-or-create the Task-<Key> Permission Group for this customer, granting its capability bundle once. */
	_ensureTaskGroup(pTask)
	{
		const tmpState = this.state;
		const tmpExisting = tmpState.TaskGroupsByKey[pTask.Key];
		if (tmpExisting) { return Promise.resolve(tmpExisting); }
		const tmpCustomer = this.callerCustomer();
		const tmpBody = { Name: 'Task-' + pTask.Key, Type: 'PermissionGroup', Description: pTask.Label + ' (managed by Entitlements)', IDCustomer: tmpCustomer };
		return this._postJSON('Role', tmpBody).then((pRes) =>
		{
			const tmpGroup = pRes && pRes.result;
			if (pRes.error || !tmpGroup || !tmpGroup.IDRole) { return null; }
			tmpState.TaskGroupsByKey[pTask.Key] = tmpGroup;
			// Grant each capability to the new group at the customer scope (VERIFY scope vs #/Access _scope() on QA).
			const tmpGrants = libTaskCatalog.taskGrants(pTask);
			return Promise.all(tmpGrants.map((pGrant) => this._getJSON(`Permissions/Grant/${encodeURIComponent(pGrant.Hash)}/To/Role/${tmpGroup.IDRole}/For/Customer/${tmpCustomer}`)))
				.then(() => tmpGroup);
		});
	}

	/** Compose (Role/Group subject) or assign (User subject) the task group onto the subject. */
	_linkSubjectToGroup(pGroupID)
	{
		const tmpState = this.state;
		const tmpCustomer = this.callerCustomer();
		if (tmpState.SubjectKind === 'User')
		{
			return this._putJSON('Permissions/Role/For/User', { IDRole: pGroupID, IDUser: tmpState.SubjectID, Context: 'Customer', IDContext: tmpCustomer }).then((pRes) => !pRes.error);
		}
		return this._postJSON('RoleEdge', { ParentIDRole: tmpState.SubjectID, ChildIDRole: pGroupID, IDCustomer: tmpCustomer }).then((pRes) => !pRes.error);
	}

	/** Remove the subject's link to a task group (the RoleEdge row, or the UserRoleJoin). */
	_unlinkSubjectFromGroup(pLink)
	{
		const tmpState = this.state;
		const tmpRC = this.restClient;
		if (!pLink || !tmpRC || (typeof tmpRC.deleteEntity !== 'function')) { return Promise.resolve(false); }
		if (tmpState.SubjectKind === 'User')
		{
			return new Promise((resolve) => tmpRC.deleteEntity('UserRoleJoin', pLink.IDUserRoleJoin, (pErr) => resolve(!pErr)));
		}
		return new Promise((resolve) => tmpRC.deleteEntity('RoleEdge', pLink.IDRoleEdge, (pErr) => resolve(!pErr)));
	}

	// ── Reports & Forms tab ──────────────────────────────────────────────────────

	/** Switch the detail tab. Loads the customer's reports/forms on first visit to the Forms tab (Master Lists is
	 * catalog-driven + reads the subject's already-loaded held groups, so it needs no per-tab fetch). */
	/** Switch tabs via a stable route (#/Entitlements/<Kind>/<ID>/<tab>) so the selected subject AND its tab both
	 *  live in the URL. The tab buttons call this. */
	navigateTab(pTab)
	{
		const tmpState = this.state;
		if (tmpState.SubjectKind && tmpState.SubjectID && (typeof window !== 'undefined') && window.location)
		{
			window.location.hash = `#/Entitlements/${ tmpState.SubjectKind }/${ tmpState.SubjectID }/${ pTab }`;
		}
		else { this.setTab(pTab); }
	}

	/** Select a subject via a stable route (keeps the current tab). The rail items call this. */
	navigateSubject(pKind, pID)
	{
		const tmpTab = this.state.Tab || 'features';
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = `#/Entitlements/${ pKind }/${ Number(pID) }/${ tmpTab }`;
		}
		else { this.selectSubject(pKind, pID); }
	}

	setTab(pTab)
	{
		const tmpState = this.state;
		const tmpTab = ((pTab === 'forms') || (pTab === 'masterlists') || (pTab === 'contractadmin')) ? pTab : 'features';
		if (tmpState.Tab === tmpTab) { return; }
		tmpState.Tab = tmpTab;
		tmpState.Error = '';
		if ((tmpTab === 'forms') && !tmpState.FormsLoaded && !tmpState.FormsLoading) { this._loadFormsAsync(); return; }
		this.render();
	}

	/** Load the customer's document types (ReportNamedInstance) — the rows of the Forms tab. */
	_loadFormsAsync()
	{
		const tmpState = this.state;
		if (tmpState.FormsLoading) { return; }
		tmpState.FormsLoading = true;
		this.render();
		this._getJSON(`ReportNamedInstances/By/IDCustomer/${this.callerCustomer()}/0/1000`).then((pRows) =>
		{
			const tmpForms = (Array.isArray(pRows) ? pRows : []).filter((pRNI) => pRNI && pRNI.Hash);
			tmpForms.sort((pA, pB) => String(pA.Name || pA.Hash).localeCompare(String(pB.Name || pB.Hash)));
			tmpState.Forms = tmpForms;
			tmpState.FormsLoaded = true;
			tmpState.FormsLoading = false;
			this._computeActiveForms();
			this.render();
		});
	}

	/** Mark each form on/off for the current subject: on when the subject holds that form's Form-<Hash> group. */
	_computeActiveForms()
	{
		const tmpState = this.state;
		const tmpActive = {};
		(tmpState.Forms || []).forEach((pRNI) =>
		{
			const tmpGroup = tmpState.FormGroupsByHash[pRNI.Hash];
			const tmpLink = (tmpGroup && tmpState.HeldRoleIDs) ? tmpState.HeldRoleIDs[Number(tmpGroup.IDRole)] : null;
			tmpActive[pRNI.Hash] = { On: !!tmpLink, Link: tmpLink || null };
		});
		tmpState.ActiveForms = tmpActive;
	}

	setFormSearch(pValue)
	{
		const tmpState = this.state;
		tmpState.FormSearch = String(pValue == null ? '' : pValue);
		// Update just the list, so the search input keeps focus (a full render would rebuild the input).
		if (this.pict.ContentAssignment.getElement(FORMS_LIST_MOUNT).length > 0)
		{
			this.pict.ContentAssignment.assignContent(FORMS_LIST_MOUNT, this._formsListHTML(this.canEdit()));
		}
		else { this.render(); }
	}

	/** Toggle full access to a form for the subject — same group-compose path as a feature task. */
	toggleForm(pHash)
	{
		const tmpState = this.state;
		if (!this.canEdit() || !tmpState.SubjectID || tmpState.Busy) { return; }
		const tmpRNI = (tmpState.Forms || []).find((pRNI) => pRNI.Hash === pHash);
		if (!tmpRNI) { return; }
		const tmpEntry = tmpState.ActiveForms[pHash] || { On: false };
		tmpState.Busy = true;
		tmpState.Error = '';
		this._repaintActiveList();
		const fThen = (pOk) =>
		{
			tmpState.Busy = false;
			if (!pOk) { tmpState.Error = 'The server rejected the change (you may lack Permission-Grant in this context). Nothing was changed.'; this._repaintActiveList(); return; }
			this._loadSubjectTasks(tmpState.SubjectKind, tmpState.SubjectID, true);   // reloads held groups → recomputes both tabs
		};
		if (!tmpEntry.On)
		{
			this._ensureFormGroup(tmpRNI).then((pGroup) => (pGroup ? this._linkSubjectToGroup(pGroup.IDRole) : false)).then(fThen);
		}
		else
		{
			this._unlinkSubjectFromGroup(tmpEntry.Link).then(fThen);
		}
	}

	/** Find-or-create the Form-<Hash> Permission Group for this customer, granting full CRUD on the document type. */
	_ensureFormGroup(pRNI)
	{
		const tmpState = this.state;
		const tmpExisting = tmpState.FormGroupsByHash[pRNI.Hash];
		if (tmpExisting) { return Promise.resolve(tmpExisting); }
		const tmpCustomer = this.callerCustomer();
		const tmpBody = { Name: FORM_PREFIX + pRNI.Hash, Type: 'PermissionGroup', Description: `Form: ${pRNI.Name || pRNI.Hash} (managed by Entitlements)`, IDCustomer: tmpCustomer };
		return this._postJSON('Role', tmpBody).then((pRes) =>
		{
			const tmpGroup = pRes && pRes.result;
			if (pRes.error || !tmpGroup || !tmpGroup.IDRole) { return null; }
			tmpState.FormGroupsByHash[pRNI.Hash] = tmpGroup;
			// Full access = the four base report actions on this document type, at the customer scope (matching the
			// Task groups + the resolver's Customer/<customer> check). Per-action / per-context nuance → #/Access.
			return Promise.all(FORM_ACTIONS.map((pAction) => this._getJSON(`Permissions/Grant/${encodeURIComponent(REPORT_ENTITY_PREFIX + pRNI.Hash + '-' + pAction)}/To/Role/${tmpGroup.IDRole}/For/Customer/${tmpCustomer}`)))
				.then(() => tmpGroup);
		});
	}

	// ── Master Lists tab ─────────────────────────────────────────────────────────

	/** The master lists an admin can grant access to — the static seed catalog (Headlight-MasterLists.js). */
	_masterListCatalog()
	{
		return (Array.isArray(libMasterListCatalog) ? libMasterListCatalog : []).filter((pEntry) => pEntry && pEntry.Entity);
	}

	/** Group the catalog by Domain for display: [ { Domain, Lists:[entry…] } ], domains A→Z with "Other" last. */
	_masterListDomains()
	{
		const tmpByDomain = {};
		const tmpOrder = [];
		this._masterListCatalog().forEach((pEntry) =>
		{
			const tmpDomain = pEntry.Domain || 'Other';
			if (!tmpByDomain[tmpDomain]) { tmpByDomain[tmpDomain] = []; tmpOrder.push(tmpDomain); }
			tmpByDomain[tmpDomain].push(pEntry);
		});
		tmpOrder.sort((pA, pB) => (pA === 'Other') ? 1 : (pB === 'Other') ? -1 : pA.localeCompare(pB));
		return tmpOrder.map((pDomain) => ({ Domain: pDomain, Lists: tmpByDomain[pDomain].slice().sort((pA, pB) => String(pA.Label || pA.Entity).localeCompare(String(pB.Label || pB.Entity))) }));
	}

	/** Mark each (entity, action) on/off for the subject: on when it holds that MList-<Entity>-<Action> group. */
	_computeActiveMasterLists()
	{
		const tmpState = this.state;
		const tmpActive = {};
		this._masterListCatalog().forEach((pEntry) =>
		{
			MLIST_ACTIONS.forEach((pAction) =>
			{
				const tmpKey = `${pEntry.Entity}-${pAction}`;
				const tmpGroup = tmpState.MasterListGroupsByKey[tmpKey];
				const tmpLink = (tmpGroup && tmpState.HeldRoleIDs) ? tmpState.HeldRoleIDs[Number(tmpGroup.IDRole)] : null;
				tmpActive[tmpKey] = { On: !!tmpLink, Link: tmpLink || null };
			});
		});
		tmpState.ActiveMasterLists = tmpActive;
	}

	setMasterListSearch(pValue)
	{
		const tmpState = this.state;
		tmpState.MasterListSearch = String(pValue == null ? '' : pValue);
		// Repaint just the list so the search input keeps focus (mirrors setFormSearch).
		if (this.pict.ContentAssignment.getElement(MLIST_LIST_MOUNT).length > 0)
		{
			this.pict.ContentAssignment.assignContent(MLIST_LIST_MOUNT, this._masterListsListHTML(this.canEdit()));
		}
		else { this.render(); }
	}

	/** Show/hide the Create/Update/Delete disclosure for one master list (client-only UI state; repaint the list). */
	toggleMasterListAdvanced(pEntity)
	{
		const tmpState = this.state;
		if (tmpState.MasterListExpanded[pEntity]) { delete tmpState.MasterListExpanded[pEntity]; }
		else { tmpState.MasterListExpanded[pEntity] = true; }
		this._repaintActiveList();   // repaint the list in place, preserving its scroll position (no full re-render)
	}

	/** Toggle one <Entity>-<Action> capability for the subject — same group-compose path as a feature task / form. */
	toggleMasterList(pEntity, pAction)
	{
		const tmpState = this.state;
		if (!this.canEdit() || !tmpState.SubjectID || tmpState.Busy) { return; }
		if (MLIST_ACTIONS.indexOf(pAction) < 0) { return; }
		const tmpKey = `${pEntity}-${pAction}`;
		const tmpEntry = tmpState.ActiveMasterLists[tmpKey] || { On: false };
		tmpState.Busy = true;
		tmpState.Error = '';
		this._repaintActiveList();
		const fThen = (pOk) =>
		{
			tmpState.Busy = false;
			if (!pOk) { tmpState.Error = 'The server rejected the change (you may lack Permission-Grant in this context). Nothing was changed.'; this._repaintActiveList(); return; }
			this._loadSubjectTasks(tmpState.SubjectKind, tmpState.SubjectID, true);   // reloads held groups → recomputes every tab
		};
		if (!tmpEntry.On)
		{
			this._ensureMasterListGroup(pEntity, pAction).then((pGroup) => (pGroup ? this._linkSubjectToGroup(pGroup.IDRole) : false)).then(fThen);
		}
		else
		{
			this._unlinkSubjectFromGroup(tmpEntry.Link).then(fThen);
		}
	}

	/** Find-or-create the MList-<Entity>-<Action> Permission Group for this customer, granting that one capability. */
	_ensureMasterListGroup(pEntity, pAction)
	{
		const tmpState = this.state;
		const tmpKey = `${pEntity}-${pAction}`;
		const tmpExisting = tmpState.MasterListGroupsByKey[tmpKey];
		if (tmpExisting) { return Promise.resolve(tmpExisting); }
		const tmpCustomer = this.callerCustomer();
		const tmpBody = { Name: MLIST_PREFIX + tmpKey, Type: 'PermissionGroup', Description: `Master list: ${pAction} ${pEntity} (managed by Entitlements)`, IDCustomer: tmpCustomer };
		return this._postJSON('Role', tmpBody).then((pRes) =>
		{
			const tmpGroup = pRes && pRes.result;
			if (pRes.error || !tmpGroup || !tmpGroup.IDRole) { return null; }
			tmpState.MasterListGroupsByKey[tmpKey] = tmpGroup;
			// Grant the single <Entity>-<Action> capability to the new group at the customer scope (same shape as the
			// Task/Form groups + the resolver's Customer/<customer> check).
			return this._getJSON(`Permissions/Grant/${encodeURIComponent(tmpKey)}/To/Role/${tmpGroup.IDRole}/For/Customer/${tmpCustomer}`).then(() => tmpGroup);
		});
	}

	_masterListsHTML(pEditable)
	{
		const tmpState = this.state;
		return `<p class="ent-note">Grant this ${this._subjectKindNoun()} access to a reference / catalog list. <strong>Read</strong> lets them open the list and view its records (and surfaces it in their navigation); <strong>Advanced</strong> adds create, update, and delete. For finer control (per-project scope, other entities) use the Access &amp; Permissions grid.</p>`
			+ `<input class="ent-forms-search" type="text" autocomplete="off" spellcheck="false" placeholder="Search master lists…" value="${this._esc(tmpState.MasterListSearch || '')}" oninput="_Pict.providers.Entitlements.setMasterListSearch(this.value)" />`
			+ `<div id="Ent-MList-List" class="ent-forms-list">${this._masterListsListHTML(pEditable)}</div>`;
	}

	_masterListsListHTML(pEditable)
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.MasterListSearch || '').trim().toLowerCase();
		const fMatch = (pEntry) => !tmpQuery
			|| (String(pEntry.Label || '').toLowerCase().indexOf(tmpQuery) > -1)
			|| (String(pEntry.Entity || '').toLowerCase().indexOf(tmpQuery) > -1)
			|| (String(pEntry.Domain || '').toLowerCase().indexOf(tmpQuery) > -1)
			|| (String(pEntry.Keywords || '').toLowerCase().indexOf(tmpQuery) > -1);
		const tmpDomains = this._masterListDomains()
			.map((pDomain) => ({ Domain: pDomain.Domain, Lists: pDomain.Lists.filter(fMatch) }))
			.filter((pDomain) => pDomain.Lists.length > 0);
		if (!tmpDomains.length) { return `<div class="ent-rail-empty">${tmpQuery ? 'No master lists match your search.' : 'No master lists found.'}</div>`; }
		const tmpDisabled = !pEditable || tmpState.Busy;
		return tmpDomains.map((pDomain) =>
		{
			const tmpRows = pDomain.Lists.map((pEntry) => this._masterListRowHTML(pEntry, tmpDisabled)).join('');
			return `<div class="ent-area"><div class="ent-area-head">${this._esc(pDomain.Domain)}</div>${tmpRows}</div>`;
		}).join('');
	}

	_masterListRowHTML(pEntry, pDisabled)
	{
		const tmpState = this.state;
		const tmpEntity = pEntry.Entity;
		const tmpReadEntry = tmpState.ActiveMasterLists[`${tmpEntity}-Read`] || { On: false };
		const tmpExpanded = !!tmpState.MasterListExpanded[tmpEntity];
		const tmpAdvOnCount = MLIST_ADV_ACTIONS.filter((pAction) => { const tmpE = tmpState.ActiveMasterLists[`${tmpEntity}-${pAction}`]; return tmpE && tmpE.On; }).length;
		const tmpIcon = pEntry.Icon ? `<i class="${this._esc(pEntry.Icon)} ent-mlist-icon"></i>` : '';
		const tmpReadToggle = `<button class="ent-switch${tmpReadEntry.On ? ' is-on' : ''}"${pDisabled ? ' disabled' : ''} title="Read — view this list and its records" onclick="_Pict.providers.Entitlements.toggleMasterList('${this._esc(tmpEntity)}','Read'); return false;"><span class="ent-switch-dot"></span></button>`;
		const tmpAdvBtn = `<button class="ent-mlist-adv-btn" onclick="_Pict.providers.Entitlements.toggleMasterListAdvanced('${this._esc(tmpEntity)}'); return false;">${tmpExpanded ? '▾' : '▸'} Advanced${tmpAdvOnCount ? ` <span class="ent-mlist-adv-count">${tmpAdvOnCount}</span>` : ''}</button>`;
		const tmpAdvRow = tmpExpanded
			? `<div class="ent-mlist-adv">${MLIST_ADV_ACTIONS.map((pAction) =>
			{
				const tmpEntry = tmpState.ActiveMasterLists[`${tmpEntity}-${pAction}`] || { On: false };
				return `<div class="ent-mlist-action"><span class="ent-mlist-action-lbl">${this._esc(pAction)}</span><button class="ent-switch${tmpEntry.On ? ' is-on' : ''}"${pDisabled ? ' disabled' : ''} onclick="_Pict.providers.Entitlements.toggleMasterList('${this._esc(tmpEntity)}','${pAction}'); return false;"><span class="ent-switch-dot"></span></button></div>`;
			}).join('')}</div>`
			: '';
		return `<div class="ent-task ent-mlist-row"><div class="ent-task-main"><div class="ent-task-label">${tmpIcon}${this._esc(pEntry.Label || tmpEntity)}</div>`
			+ `<div class="ent-task-desc">${this._esc(pEntry.Description || tmpEntity)}</div>${tmpAdvBtn}</div>`
			+ `<div class="ent-mlist-read"><span class="ent-read-lbl">Read</span>${tmpReadToggle}</div></div>${tmpAdvRow}`;
	}

	// ── Render (hand-built HTML into the shell's mounts) ─────────────────────────

	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(RAIL_MOUNT).length === 0) { return; }
		// Keep the rail search input alive across renders so it never loses focus mid-type (mirrors
		// SuperUserGrant-Provider): build the rail shell (search + list mount) only when it's absent, otherwise
		// repaint just the list. Keying off the list mount's presence also survives route re-navigation.
		if (tmpCA.getElement(RAIL_LIST_MOUNT).length === 0) { tmpCA.assignContent(RAIL_MOUNT, this._railHTML()); }
		else { tmpCA.assignContent(RAIL_LIST_MOUNT, this._railListHTML()); }
		tmpCA.assignContent(DETAIL_MOUNT, this._detailHTML());
	}

	// ── In-place repaints (a toggle must NOT rebuild the whole detail — that resets the active tab's scroll, the search
	//    box, and the page position). Repaint ONLY the active tab's list + the status line, preserving the list's scroll. ──
	_activeListMount() { const tmpTab = this.state.Tab; return (tmpTab === 'masterlists') ? MLIST_LIST_MOUNT : (tmpTab === 'forms') ? FORMS_LIST_MOUNT : TASKS_LIST_MOUNT; }
	_activeListHTML() { const tmpEditable = this.canEdit(); const tmpTab = this.state.Tab; return (tmpTab === 'masterlists') ? this._masterListsListHTML(tmpEditable) : (tmpTab === 'forms') ? this._formsListHTML(tmpEditable) : this._tasksListHTML(tmpEditable); }
	/** Repaint just the active tab's entitlement list (keeps its internal scroll position) + the status line. */
	_repaintActiveList()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpMount = this._activeListMount();
		const tmpEl = tmpCA.getElement(tmpMount);
		if (!tmpEl || tmpEl.length === 0) { this.render(); return; }   // mount not in the DOM (e.g. Forms still loading) — fall back
		const tmpTop = tmpEl[0].scrollTop;
		tmpCA.assignContent(tmpMount, this._activeListHTML());
		const tmpEl2 = tmpCA.getElement(tmpMount);
		if (tmpEl2 && tmpEl2.length) { tmpEl2[0].scrollTop = tmpTop; }   // restore scroll (assignContent resets it)
		this._repaintStatus();
	}
	_repaintStatus() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement(STATUS_MOUNT).length > 0) { tmpCA.assignContent(STATUS_MOUNT, this._statusHTML()); } }
	_statusHTML() { const tmpState = this.state; return (tmpState.Busy ? '<p class="ent-note ent-busy">Saving…</p>' : '') + (tmpState.Error ? `<p class="ent-error">${this._esc(tmpState.Error)}</p>` : ''); }

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }


	/**
	 * Search the subject rail. SERVER-SIDE (mirrors SuperUserGrant-Provider): a customer has thousands of users +
	 * roles, so we can't load them and filter in the browser — that only ever saw the first page. Debounce 250ms,
	 * then query the server; a stale-token guard drops out-of-order responses. Repaints only the list mount so the
	 * search input keeps focus (see render()).
	 */
	setSubjectSearch(pValue)
	{
		const tmpState = this.state;
		tmpState.SubjectSearch = String(pValue == null ? '' : pValue);
		if (this._searchTimer) { clearTimeout(this._searchTimer); }
		this._searchTimer = setTimeout(() => { this._searchTimer = null; this._searchSubjects(); }, 250);
	}

	/** Repaint just the rail list (keeps the search input alive); full render if the list mount isn't in the DOM. */
	_renderRail()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(RAIL_LIST_MOUNT).length > 0) { tmpCA.assignContent(RAIL_LIST_MOUNT, this._railListHTML()); }
		else { this.render(); }
	}

	/**
	 * Run the subject search against the server: Roles/Groups matched on Name (global + this customer, or all for a
	 * super-user); Users via the shared user search (name, login ID or email; every word must match).
	 * Managed groups (Task-/Form-/config-admin) are filtered out.
	 */
	_searchSubjects()
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.SubjectSearch || '').trim();
		if (tmpQuery.length < 2)
		{
			tmpState.Searching = false;
			tmpState.Roles = []; tmpState.Groups = []; tmpState.Users = [];
			this._renderRail();
			return;
		}
		const tmpToken = ++this._searchToken;
		tmpState.Searching = true;
		this._renderRail();
		const tmpLike = encodeURIComponent(`%${tmpQuery}%`);
		const tmpCustomer = this.callerCustomer();
		// Roles/Groups: Name LIKE, scoped to global (IDCustomer 0) + this customer unless super-user (mirrors loadAsync's
		// old scope). Users: the shared user search (name, login ID or email), scoped to this customer unless super-user.
		const tmpRoleScope = this.isSuperUser() ? '' : `~FBL~IDCustomer~INN~0,${tmpCustomer}`;
		const tmpUserScope = this.isSuperUser() ? 'FBV~Deleted~EQ~0' : `FBV~IDCustomer~EQ~${tmpCustomer}~FBV~Deleted~EQ~0`;
		const tmpRolesURL = `Roles/FilteredTo/FBV~Name~LK~${tmpLike}${tmpRoleScope}~FBV~Deleted~EQ~0/0/50`;
		const fGetJSON = (pURL) => this._getJSON(pURL);
		Promise.all([ fGetJSON(tmpRolesURL), libUserSearch.searchUsersAsync(fGetJSON, tmpQuery, { Scope: tmpUserScope, PageSize: 50 }) ]).then((pResults) =>
		{
			if (tmpToken !== this._searchToken)
			{
				return;   // a newer search superseded this one
			}
			const fManaged = (pRole) => { const tmpName = String((pRole && pRole.Name) || ''); return (tmpName.indexOf(TASK_PREFIX) === 0) || (tmpName.indexOf(FORM_PREFIX) === 0) || (tmpName.indexOf(MLIST_PREFIX) === 0) || (tmpName === CONFIG_ADMIN_GROUP_NAME); };
			const tmpRoleRows = (Array.isArray(pResults[0]) ? pResults[0] : []).filter((pRole) => pRole && !fManaged(pRole));
			tmpState.Roles = tmpRoleRows.filter((pRole) => pRole.Type !== 'PermissionGroup');
			tmpState.Groups = tmpRoleRows.filter((pRole) => pRole.Type === 'PermissionGroup');
			tmpState.Users = pResults[1];
			tmpState.Searching = false;
			this._renderRail();
		});
	}

	_railGroup(pTitle, pIcon, pItems, pKind, pIDField, pLabelFn)
	{
		const tmpState = this.state;
		const tmpRows = (pItems || []).map((pItem) =>
		{
			const tmpID = pItem[pIDField];
			const tmpActive = ((tmpState.SubjectKind === pKind) && (Number(tmpState.SubjectID) === Number(tmpID))) ? ' is-active' : '';
			return `<div class="ent-rail-item${tmpActive}" onclick="_Pict.providers.Entitlements.navigateSubject('${pKind}', ${Number(tmpID)})">${this._esc(pLabelFn(pItem))}</div>`;
		}).join('');
		return `<div class="ent-rail-group"><div class="ent-rail-head"><i class="ent-rail-head-icon ${pIcon}"></i><span class="ent-rail-head-text">${this._esc(pTitle)}</span><span class="ent-rail-count">${(pItems || []).length}</span></div>${tmpRows || '<div class="ent-rail-empty">None</div>'}</div>`;
	}

	_railHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Loaded) { return '<div class="ent-rail-empty">Loading…</div>'; }
		return `<input class="ent-rail-search" type="text" autocomplete="off" spellcheck="false" placeholder="Search roles, groups, users…" value="${this._esc(tmpState.SubjectSearch || '')}" oninput="_Pict.providers.Entitlements.setSubjectSearch(this.value)" />`
			+ `<div id="Ent-Rail-List" class="ent-rail-list">${this._railListHTML()}</div>`;
	}

	/** The subject rail: server search results (Roles / Permission Groups / Users), or a prompt/empty state. */
	_railListHTML()
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.SubjectSearch || '').trim();
		if (tmpState.Searching) { return '<div class="ent-rail-empty">Searching…</div>'; }
		if (tmpQuery.length < 2) { return '<div class="ent-rail-empty">Type at least 2 characters to find a role, permission group, or user.</div>'; }
		const fRoleLabel = (pItem) => pItem.Name || ('Role #' + pItem.IDRole);
		const fGroupLabel = (pItem) => pItem.Name || ('Group #' + pItem.IDRole);
		const fUserLabel = (pItem) => libUserSearch.userLabel(pItem);
		const tmpParts = [];
		// Only render a group that has hits — during search an empty "Roles — None" band is just noise.
		if ((tmpState.Roles || []).length) { tmpParts.push(this._railGroup('Roles', 'fas fa-user-shield', tmpState.Roles, 'Role', 'IDRole', fRoleLabel)); }
		if ((tmpState.Groups || []).length) { tmpParts.push(this._railGroup('Permission Groups', 'fas fa-layer-group', tmpState.Groups, 'Group', 'IDRole', fGroupLabel)); }
		if ((tmpState.Users || []).length) { tmpParts.push(this._railGroup('Users', 'fas fa-users', tmpState.Users, 'User', 'IDUser', fUserLabel)); }
		if (!tmpParts.length) { return `<div class="ent-rail-empty">No roles, permission groups, or users match “${this._esc(tmpQuery)}”.</div>`; }
		return tmpParts.join('');
	}

	_detailHTML()
	{
		const tmpState = this.state;
		if (!tmpState.SubjectID) { return '<div class="ent-empty">Pick a role, permission group, or user on the left to set up what they can do.</div>'; }
		const tmpEditable = this.canEdit();
		const tmpKindLabel = (tmpState.SubjectKind === 'User') ? 'User' : (tmpState.SubjectKind === 'Group') ? 'Permission Group' : 'Role';
		const tmpReadonly = tmpEditable ? '' : '<p class="ent-note">You can view entitlements here, but changing them needs a Role Manager role.</p>';
		const tmpBody = (tmpState.Tab === 'forms')
			? this._formsHTML(tmpEditable)
			: (tmpState.Tab === 'masterlists')
				? this._masterListsHTML(tmpEditable)
				: (tmpState.Tab === 'contractadmin')
					? this._contractAdminHTML(tmpEditable)
					: this._featuresHTML(tmpEditable);
		return `<div class="ent-detail-head"><div class="ent-detail-title">${this._esc(tmpState.SubjectLabel)}</div>`
			+ `<div class="ent-detail-sub">${this._esc(tmpKindLabel)}</div></div>`
			+ this._tabBarHTML()
			+ tmpReadonly
			+ `<div id="Ent-Status">${this._statusHTML()}</div>`
			+ tmpBody;
	}

	_tabBarHTML()
	{
		const tmpTab = this.state.Tab || 'features';
		return libTabBar.renderTabsHTML(
			[
				{ Key: 'features', Label: 'Features', Icon: 'fas fa-toggle-on' },
				{ Key: 'masterlists', Label: 'Master Lists', Icon: 'fas fa-database' },
				{ Key: 'contractadmin', Label: 'Contract Administration', Icon: 'fas fa-file-invoice-dollar' },
				{ Key: 'forms', Label: 'Reports & Forms', Icon: 'fas fa-file-lines' },
			],
			tmpTab,
			(pTab) => `_Pict.providers.Entitlements.navigateTab('${ pTab.Key }'); return false;`,
			{ overflow: 'wrap' });
	}

	_subjectKindNoun() { const tmpKind = this.state.SubjectKind; return (tmpKind === 'User') ? 'user' : (tmpKind === 'Group') ? 'permission group' : 'role'; }

	_formsHTML(pEditable)
	{
		const tmpState = this.state;
		if (tmpState.FormsLoading || !tmpState.FormsLoaded) { return '<p class="ent-note">Loading reports &amp; forms…</p>'; }
		return `<p class="ent-note">Turn a report or form on to give this ${this._subjectKindNoun()} full access to it — create, view, update, and delete. For finer control (individual actions, per-project scope, or approval statuses) use the Access &amp; Permissions grid.</p>`
			+ `<input class="ent-forms-search" type="text" autocomplete="off" spellcheck="false" placeholder="Search reports &amp; forms…" value="${this._esc(tmpState.FormSearch || '')}" oninput="_Pict.providers.Entitlements.setFormSearch(this.value)" />`
			+ `<div id="Ent-Forms-List" class="ent-forms-list">${this._formsListHTML(pEditable)}</div>`;
	}

	_formsListHTML(pEditable)
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.FormSearch || '').trim().toLowerCase();
		const tmpForms = (tmpState.Forms || []).filter((pRNI) => !tmpQuery
			|| (String(pRNI.Name || '').toLowerCase().indexOf(tmpQuery) > -1)
			|| (String(pRNI.Hash || '').toLowerCase().indexOf(tmpQuery) > -1));
		if (!tmpForms.length) { return `<div class="ent-rail-empty">${tmpQuery ? 'No reports or forms match your search.' : 'No reports or forms found for this customer.'}</div>`; }
		return tmpForms.map((pRNI) =>
		{
			const tmpEntry = tmpState.ActiveForms[pRNI.Hash] || { On: false };
			const tmpDisabled = !pEditable || tmpState.Busy;
			const tmpToggle = `<button class="ent-switch${tmpEntry.On ? ' is-on' : ''}"${tmpDisabled ? ' disabled' : ''} onclick="_Pict.providers.Entitlements.toggleForm('${this._esc(pRNI.Hash)}'); return false;"><span class="ent-switch-dot"></span></button>`;
			return `<div class="ent-task"><div class="ent-task-main"><div class="ent-task-label">${this._esc(pRNI.Name || pRNI.Hash)}</div>`
				+ `<div class="ent-task-desc">${this._esc(pRNI.Hash)}</div></div>${tmpToggle}</div>`;
		}).join('');
	}

	_areaHTML(pArea, pEditable)
	{
		const tmpState = this.state;
		const tmpRows = pArea.Tasks.map((pTask) =>
		{
			const tmpEntry = tmpState.ActiveTasks[pTask.Key] || { On: false };
			// The CustomerAdmin config-admin tier CAN be granted to a person (via the shared group); other broad
			// tiers stay role/group-only, so only those are disabled for a User subject.
			const tmpUnsupportedBroadForUser = pTask.RequiresPermissionLevel && (pTask.RequiresPermissionLevel !== CONFIG_ADMIN_LEVEL) && (tmpState.SubjectKind === 'User');
			const tmpDisabled = !pEditable || tmpState.Busy || tmpUnsupportedBroadForUser || !!pTask.Pending;
			const tmpConfirm = (tmpState.ConfirmBroad === pTask.Key);
			const tmpToggle = tmpConfirm
				? `<span class="ent-confirm">Grant broad rights? <button class="ent-btn ent-btn-danger" onclick="_Pict.providers.Entitlements.toggleTask('${this._esc(pTask.Key)}')">Grant</button> <button class="ent-btn ent-btn-ghost" onclick="_Pict.providers.Entitlements.cancelBroad()">Cancel</button></span>`
				: `<button class="ent-switch${tmpEntry.On ? ' is-on' : ''}"${tmpDisabled ? ' disabled' : ''} onclick="_Pict.providers.Entitlements.toggleTask('${this._esc(pTask.Key)}'); return false;"><span class="ent-switch-dot"></span></button>`;
			const tmpFlags = (pTask.Broad ? '<span class="ent-flag">Broad</span>' : '')
				+ (pTask.Pending ? '<span class="ent-hint">Not yet available — platform update pending</span>' : '')
				+ (tmpUnsupportedBroadForUser ? '<span class="ent-hint">Set on a role, not a person</span>' : '');
			return `<div class="ent-task"><div class="ent-task-main"><div class="ent-task-label">${this._esc(pTask.Label)} ${tmpFlags}</div>`
				+ `<div class="ent-task-desc">${this._esc(pTask.Description || '')}</div></div>${tmpToggle}</div>`;
		}).join('');
		return `<div class="ent-area"><div class="ent-area-head">${this._esc(pArea.Area)}</div>${tmpRows}</div>`;
	}

	/** The Features tab: a filter box + the standard feature-task list (everything except the Contract Administration area). */
	_featuresHTML(pEditable)
	{
		const tmpState = this.state;
		return `<input class="ent-forms-search" type="text" autocomplete="off" spellcheck="false" placeholder="Filter entitlements…" value="${this._esc(tmpState.TaskSearch || '')}" oninput="_Pict.providers.Entitlements.setTaskSearch(this.value)" />`
			+ `<div id="Ent-Tasks-List" class="ent-forms-list ent-tasks">${this._tasksListHTML(pEditable)}</div>`;
	}

	/** The task rows for the active tasks-based tab (Features, or Contract Administration), filtered by the tab's filter box. */
	_tasksListHTML(pEditable)
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.TaskSearch || '').trim().toLowerCase();
		const tmpContract = (tmpState.Tab === 'contractadmin');
		const fMatch = (pTask) => !tmpQuery
			|| (String(pTask.Label || '').toLowerCase().indexOf(tmpQuery) > -1)
			|| (String(pTask.Description || '').toLowerCase().indexOf(tmpQuery) > -1)
			|| (String(pTask.Key || '').toLowerCase().indexOf(tmpQuery) > -1);
		const tmpAreas = libTaskCatalog.tasksByArea()
			.filter((pArea) => (tmpContract ? (pArea.Area === CONTRACT_ADMIN_AREA) : (pArea.Area !== CONTRACT_ADMIN_AREA)))
			.map((pArea) => ({ Area: pArea.Area, Tasks: pArea.Tasks.filter(fMatch) }))
			.filter((pArea) => pArea.Tasks.length > 0);
		if (!tmpAreas.length) { return `<div class="ent-rail-empty">${tmpQuery ? 'No entitlements match your filter.' : 'No entitlements are configured.'}</div>`; }
		return tmpAreas.map((pArea) => this._areaHTML(pArea, pEditable)).join('');
	}

	/** The dedicated Contract Administration tab: an intro + a filter box + only the Contract Administration Area tasks,
	 *  reusing the standard task toggles + list mount. */
	_contractAdminHTML(pEditable)
	{
		const tmpState = this.state;
		if (!libTaskCatalog.tasksByArea().some((pArea) => pArea.Area === CONTRACT_ADMIN_AREA)) { return '<div class="ent-empty">No Contract Administration entitlements are configured.</div>'; }
		const tmpIntro = '<p class="ent-note">Grants for the funding &amp; contract-administration arc: manage the financial reference lists (funds, funding sources, categories, accounts), wire funding to projects &amp; line items, and open the module. Per-action (Read/Create/Update/Delete) toggles for each list also live on the Master Lists tab.</p>';
		return tmpIntro
			+ `<input class="ent-forms-search" type="text" autocomplete="off" spellcheck="false" placeholder="Filter entitlements…" value="${this._esc(tmpState.TaskSearch || '')}" oninput="_Pict.providers.Entitlements.setTaskSearch(this.value)" />`
			+ `<div id="Ent-Tasks-List" class="ent-forms-list ent-tasks">${this._tasksListHTML(pEditable)}</div>`;
	}

	/** Filter the feature/contract task list in place (keeps the filter input focused during typing). */
	setTaskSearch(pValue)
	{
		const tmpState = this.state;
		tmpState.TaskSearch = String(pValue == null ? '' : pValue);
		if (this.pict.ContentAssignment.getElement(TASKS_LIST_MOUNT).length > 0)
		{
			this.pict.ContentAssignment.assignContent(TASKS_LIST_MOUNT, this._tasksListHTML(this.canEdit()));
		}
		else { this.render(); }
	}

	cancelBroad() { this.state.ConfirmBroad = ''; this._repaintActiveList(); }
}

module.exports = EntitlementsProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'Entitlements',
	AutoInitialize: true,
	AutoInitializeOrdinal: 40,
};
